/**
 * #3981 — "Name your workspace" gate (WorkspaceNameSetupScreen, App.tsx).
 *
 * Generalizes the #3949/#3968 team-only naming gate to every plan tier: any
 * OWNER whose selected workspace is still literally named "Personal" is shown
 * the (skippable) naming screen before the chat loads — otherwise the welcome
 * beat greets them with "Welcome to Personal."
 *
 * Scope clause — why `(team || createdRecently || onboarding pending)`:
 *
 * - `type === 'team'` preserves #3968 exactly: a team workspace still named
 *   "Personal" is ALWAYS prompted (covers the legacy-upgrade persona whose
 *   workspace is older than the recency window). Deliberately NO
 *   onboarding-state requirement for this arm (#3949): owners have no
 *   membership row, so the containers endpoint ORs the user-level
 *   `users.onboarding_completed` boolean into `onboardingCompleted` — and
 *   every payment path sets that boolean TRUE at payment time. Requiring it
 *   to be false would suppress the screen for the exact persona the team fix
 *   targets.
 * - `createdRecently` (workspace `created_at` within RECENT_WORKSPACE_WINDOW_MS,
 *   surfaced as `member_since`/`memberSince` by the containers endpoint)
 *   extends the gate to fresh free/pro signups — including those whose
 *   payment already flipped `users.onboarding_completed` to true.
 * - `onboardingCompleted !== true` additionally covers users mid-onboarding
 *   whose workspace predates the window. It can only WIDEN the gate (it is a
 *   disjunct, never a required conjunct), so it cannot reintroduce the #3949
 *   suppression bug.
 *
 * Long-standing personal workspaces (older than the window, owner already
 * onboarded) stay out of the gate even though most are literally named
 * "Personal" — legacy users are never prompted.
 *
 * The remaining conditions — owner + still named "Personal" + not
 * dismissed/skipped — encode "unnamed workspace": a save renames it out of
 * the gate, a skip sets the per-workspace localStorage flag, and dismissal
 * covers the session. Add-Workspace flow workspaces never match (they are
 * named at creation, defaulting to "New Workspace").
 */

/** How long after workspace creation a still-unnamed workspace counts as "fresh". */
export const RECENT_WORKSPACE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Default name given to a workspace at signup (space-service.ts). */
const DEFAULT_WORKSPACE_NAME = "Personal";

export interface WorkspaceNameGateInput {
  /** Contract workspace type from the containers endpoint. */
  type: "solo" | "team" | undefined;
  isOwner: boolean | undefined;
  containerName: string | null | undefined;
  /** Workspace `created_at` (epoch ms), surfaced as `member_since`. */
  memberSince: number | null | undefined;
  /**
   * Derived per-container flag — for owners this is just the user-level
   * `users.onboarding_completed` boolean (no membership row exists). Used
   * only as a widening disjunct; see module doc.
   */
  onboardingCompleted: boolean | undefined;
  /** In-session dismissal (X / skip already clicked this session). */
  dismissed: boolean;
  /** Per-workspace localStorage skip flag (see hasWorkspaceNameSkipFlag). */
  localStorageSkipFlag: boolean;
  /** Injectable clock for tests. */
  now?: number;
}

export function shouldShowWorkspaceNameSetup(
  input: WorkspaceNameGateInput,
): boolean {
  const now = input.now ?? Date.now();
  const createdRecently =
    typeof input.memberSince === "number" &&
    input.memberSince > 0 &&
    now - input.memberSince < RECENT_WORKSPACE_WINDOW_MS;
  return (
    input.isOwner === true &&
    (input.containerName ?? DEFAULT_WORKSPACE_NAME) ===
      DEFAULT_WORKSPACE_NAME &&
    (input.type === "team" ||
      createdRecently ||
      input.onboardingCompleted !== true) &&
    !input.dismissed &&
    !input.localStorageSkipFlag
  );
}

/**
 * localStorage keys for the per-workspace "user skipped naming" flag.
 * `xavior:team-named:<id>` is the legacy key written by the team-only gate
 * (#3949) — still honored so users who skipped before this generalization
 * are not re-prompted. New skips write the generalized key.
 */
export function workspaceNameSkipKeys(workspaceId: string): {
  current: string;
  legacy: string;
} {
  return {
    current: `xavior:workspace-named:${workspaceId}`,
    legacy: `xavior:team-named:${workspaceId}`,
  };
}

export function hasWorkspaceNameSkipFlag(
  workspaceId: string | null | undefined,
): boolean {
  if (typeof window === "undefined" || !workspaceId) return false;
  try {
    const keys = workspaceNameSkipKeys(workspaceId);
    return Boolean(
      localStorage.getItem(keys.current) || localStorage.getItem(keys.legacy),
    );
  } catch {
    return false;
  }
}

export function setWorkspaceNameSkipFlag(
  workspaceId: string | null | undefined,
): void {
  if (typeof window === "undefined" || !workspaceId) return;
  try {
    localStorage.setItem(workspaceNameSkipKeys(workspaceId).current, "1");
  } catch {
    /* noop */
  }
}
